// client/src/pages/DeliveryPartners.js
import React, { useState, useEffect, useContext, useCallback } from 'react';
import { AuthContext } from '../context/AuthContext';
import {
  Box, Typography, Table, TableContainer, TableHead, TableRow, TableCell, TableBody, Button, IconButton,
  Stack, Alert, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Link as MuiLink,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlineOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import LanguageIcon from '@mui/icons-material/Language';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import EmptyState from '../components/EmptyState';
import { API_BASE_URL } from '../utils/media';

const emptyForm = { name: '', phone: '', email: '', website: '', notes: '' };

const DeliveryPartners = () => {
  const [couriers, setCouriers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editingCourier, setEditingCourier] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [formError, setFormError] = useState('');
  const { token, user } = useContext(AuthContext);
  const isAdmin = user?.role === 'admin';

  const fetchCouriers = useCallback(async () => {
    if (!token) return;
    try {
      const response = await fetch(`${API_BASE_URL}/api/couriers`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to fetch delivery partners.');
      setCouriers(await response.json());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchCouriers(); }, [fetchCouriers]);

  const openCreate = () => { setEditingCourier(null); setFormData(emptyForm); setFormError(''); setFormOpen(true); };
  const openEdit = (courier) => { setEditingCourier(courier); setFormData({ ...emptyForm, ...courier }); setFormError(''); setFormOpen(true); };

  const handleSave = async () => {
    setFormError('');
    try {
      const url = editingCourier ? `${API_BASE_URL}/api/admin/couriers/${editingCourier.id}` : `${API_BASE_URL}/api/admin/couriers`;
      const method = editingCourier ? 'PUT' : 'POST';
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(formData),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to save courier.');
      setFormOpen(false);
      fetchCouriers();
    } catch (err) {
      setFormError(err.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this delivery partner from the directory?')) return;
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/couriers/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to delete courier.');
      fetchCouriers();
    } catch (err) {
      alert(err.message);
    }
  };

  if (loading) return <CircularProgress size={28} />;
  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1} sx={{ mb: 1 }}>
        <Typography variant="h5" fontWeight={700}>Delivery Partners</Typography>
        {isAdmin && <Button variant="contained" onClick={openCreate}>Add Courier</Button>}
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Reference this list when arranging shipping for your orders.
        {isAdmin && ' Fill in the contact details you actually use for each courier — nothing here is pre-filled with real numbers.'}
      </Typography>

      {couriers.length === 0 ? (
        <EmptyState icon={<LocalShippingOutlinedIcon />} title="No delivery partners yet" description="Ask an admin to add couriers to the directory." />
      ) : (
        <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Courier</TableCell><TableCell>Contact</TableCell><TableCell>Notes</TableCell>
                {isAdmin && <TableCell align="right">Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {couriers.map((c) => (
                <TableRow key={c.id} hover>
                  <TableCell>{c.name}</TableCell>
                  <TableCell>
                    <Stack spacing={0.5}>
                      {c.phone && (
                        <Stack direction="row" spacing={0.75} alignItems="center">
                          <PhoneOutlinedIcon fontSize="inherit" />
                          <MuiLink href={`tel:${c.phone}`} variant="body2">{c.phone}</MuiLink>
                        </Stack>
                      )}
                      {c.email && (
                        <Stack direction="row" spacing={0.75} alignItems="center">
                          <EmailOutlinedIcon fontSize="inherit" />
                          <MuiLink href={`mailto:${c.email}`} variant="body2">{c.email}</MuiLink>
                        </Stack>
                      )}
                      {c.website && (
                        <Stack direction="row" spacing={0.75} alignItems="center">
                          <LanguageIcon fontSize="inherit" />
                          <MuiLink href={c.website} target="_blank" rel="noopener noreferrer" variant="body2">{c.website}</MuiLink>
                        </Stack>
                      )}
                      {!c.phone && !c.email && !c.website && (
                        <Typography variant="caption" color="text.disabled">No contact details yet</Typography>
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell><Typography variant="body2" color="text.secondary">{c.notes || '—'}</Typography></TableCell>
                  {isAdmin && (
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => openEdit(c)}><EditIcon fontSize="small" /></IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDelete(c.id)}><DeleteOutlineIcon fontSize="small" /></IconButton>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={formOpen} onClose={() => setFormOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>{editingCourier ? 'Edit Courier' : 'Add Courier'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {formError && <Alert severity="error">{formError}</Alert>}
            <TextField label="Name" fullWidth value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
            <TextField label="Phone" fullWidth value={formData.phone || ''} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} />
            <TextField label="Email" fullWidth value={formData.email || ''} onChange={(e) => setFormData({ ...formData, email: e.target.value })} />
            <TextField label="Website" fullWidth value={formData.website || ''} onChange={(e) => setFormData({ ...formData, website: e.target.value })} placeholder="https://..." />
            <TextField label="Notes (coverage area, account number, etc.)" fullWidth multiline minRows={2} value={formData.notes || ''} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFormOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={!formData.name}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DeliveryPartners;
